import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ShieldCheck, RotateCcw, Save } from "lucide-react";
import api from "@/lib/api";

const inp =
  "w-full bg-card border border-border rounded-lg px-3.5 py-2 text-sm text-foreground focus:outline-none focus:border-primary";

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || "");
}

export default function ApprovalsSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [defaults, setDefaults] = useState([]);

  const load = async () => {
    try {
      const r = await api.get("/approvals/config");
      setApprovers(r.data?.approvers || []);
      setDefaults(r.data?.defaults || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar aprovadores");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setField = (i, key, value) => {
    setApprovers(list => list.map((a, idx) => idx === i ? { ...a, [key]: value } : a));
  };

  const addRow = () => {
    setApprovers(list => [...list, { email: "", label: "" }]);
  };

  const removeRow = (i) => {
    setApprovers(list => list.filter((_, idx) => idx !== i));
  };

  const resetToDefaults = () => {
    if (!window.confirm("Restaurar lista original (Rui Pinto + Daniela Monteiro)?")) return;
    setApprovers(defaults.map(d => ({ ...d })));
  };

  const save = async () => {
    const cleaned = approvers
      .map(a => ({ email: (a.email || "").trim().toLowerCase(), label: (a.label || "").trim() }))
      .filter(a => a.email);

    if (cleaned.length === 0) {
      toast.error("Adiciona pelo menos 1 aprovador");
      return;
    }
    const bad = cleaned.find(a => !isValidEmail(a.email));
    if (bad) { toast.error(`Email inválido: ${bad.email}`); return; }
    const seen = new Set();
    for (const a of cleaned) {
      if (seen.has(a.email)) { toast.error(`Email duplicado: ${a.email}`); return; }
      seen.add(a.email);
    }

    setSaving(true);
    try {
      const r = await api.put("/approvals/config", { approvers: cleaned });
      setApprovers(r.data?.approvers || []);
      toast.success("Lista de aprovadores actualizada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="p-8 lg:p-12 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 max-w-3xl mx-auto space-y-6" data-testid="approvals-root">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Organização</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Aprovadores de Desconto</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Utilizadores autorizados a aprovar reduções da estimativa solicitadas pelo Gestor de Conta.
          Basta a aprovação de um único deles para o desconto ser aplicado.
        </p>
      </div>

      <div className="bg-card rounded-2xl border border-border p-6 lg:p-8 space-y-5">
        <div className="flex items-start gap-3 pb-4 border-b border-border">
          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--info-bg))] flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-foreground" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-xl">Lista de aprovadores</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Todos os utilizadores desta lista recebem email quando um pedido é criado. A primeira aprovação decide.
            </p>
          </div>
          <button
            onClick={resetToDefaults}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground hover:underline"
            data-testid="approvals-reset-btn"
            title="Restaurar aprovadores originais"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Repor originais
          </button>
        </div>

        <div className="space-y-2" data-testid="approvers-list">
          {approvers.map((a, i) => (
            <div key={i} className="flex gap-2 items-start" data-testid={`approver-row-${i}`}>
              <input
                type="email"
                className={inp + " flex-1"}
                value={a.email}
                onChange={e => setField(i, "email", e.target.value)}
                placeholder="email@caring.pt"
                data-testid={`approver-email-${i}`}
              />
              <input
                type="text"
                className={inp + " flex-1"}
                value={a.label}
                onChange={e => setField(i, "label", e.target.value)}
                placeholder="Nome (opcional)"
                data-testid={`approver-label-${i}`}
              />
              <button
                onClick={() => removeRow(i)}
                className="p-2 rounded-md text-muted-foreground hover:text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))]"
                title="Remover"
                data-testid={`approver-remove-${i}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          <button
            onClick={addRow}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-foreground border border-dashed border-border hover:bg-muted/40 font-semibold"
            data-testid="approver-add-btn"
          >
            <Plus className="w-4 h-4" /> Adicionar aprovador
          </button>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            {approvers.length} aprovador{approvers.length === 1 ? "" : "es"} configurado{approvers.length === 1 ? "" : "s"}
          </p>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 disabled:opacity-50"
            data-testid="approvals-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar alterações
          </button>
        </div>
      </div>

      <div className="text-xs text-foreground bg-muted/40 border border-[#E8DEC5] rounded-lg p-3">
        <strong>Dica</strong> — para experimentar sem afectar produção, adicionar temporariamente o teu próprio email.
        Se remover todos e tentar guardar, o backend recusa (mínimo 1 aprovador).
      </div>
    </div>
  );
}
